import { useMemo, useState } from 'react'
import {
  LANDING_PAGE_CAPABILITIES,
  LANDING_PAGE_CAPABILITY_DOMAINS,
  LANDING_PAGE_SECTION,
  LANDING_PAGE_UI,
} from '@propbix/constants/public'
import { Reveal } from '@/components/Reveal'

type Capability = (typeof LANDING_PAGE_CAPABILITIES)[number]
type Domain = (typeof LANDING_PAGE_CAPABILITY_DOMAINS)[number]

function CapabilityPreview({ cap }: { cap: Capability }) {
  const [hero, ...rows] = cap.previewLines

  return (
    <div className="command-desk overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 px-4 py-3.5 sm:px-5">
        <div className="min-w-0">
          <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-slate-500">
            {cap.previewTitle}
          </p>
          <p className="mt-1 truncate text-sm font-semibold tracking-tight text-white">{cap.label}</p>
        </div>
        <span className="desk-badge border border-white/10 bg-white/5 text-slate-300">
          {LANDING_PAGE_UI.showcaseExampleLabel}
        </span>
      </div>

      <div className="space-y-3 p-4 sm:p-5">
        <div className="command-widget p-4">
          <p className="text-sm leading-6 text-slate-300">{cap.summary}</p>
        </div>

        {hero !== undefined ? (
          <div className="rounded-[var(--radius-card)] border border-teal-500/25 bg-teal-500/10 px-4 py-3.5">
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-teal-300/80">
              {LANDING_PAGE_UI.capabilitiesPreviewMetricLabel}
            </p>
            <p className="mt-2 text-sm font-semibold leading-snug text-white">{hero}</p>
          </div>
        ) : null}

        <ul className="divide-y divide-white/8 overflow-hidden rounded-[var(--radius-card)] border border-white/10 bg-white/[0.03]">
          {rows.map((line, index) => (
            <li key={line} className="flex items-start gap-3 px-4 py-3">
              <span className="mt-0.5 font-mono text-[0.65rem] font-semibold text-slate-500">
                {String(index + 1).padStart(2, '0')}
              </span>
              <p className="text-sm font-medium leading-snug text-slate-100">
                <span className="sr-only">{LANDING_PAGE_UI.capabilitiesPreviewRowLabel}: </span>
                {line}
              </p>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}

export function CapabilityExplorer() {
  const [domainId, setDomainId] = useState(LANDING_PAGE_CAPABILITY_DOMAINS[0].id)
  const domain =
    LANDING_PAGE_CAPABILITY_DOMAINS.find((item) => item.id === domainId) ??
    LANDING_PAGE_CAPABILITY_DOMAINS[0]

  const domainCapabilities = useMemo(() => {
    const idSet = new Set<string>(domain.capabilityIds)
    return LANDING_PAGE_CAPABILITIES.filter((cap) => idSet.has(cap.id))
  }, [domain])

  const [selectedId, setSelectedId] = useState(domainCapabilities[0]?.id ?? LANDING_PAGE_CAPABILITIES[0].id)

  const selected =
    domainCapabilities.find((c) => c.id === selectedId) ??
    domainCapabilities[0] ??
    LANDING_PAGE_CAPABILITIES[0]

  function selectDomain(next: Domain) {
    setDomainId(next.id)
    const firstId = next.capabilityIds[0]
    const first = LANDING_PAGE_CAPABILITIES.find((cap) => cap.id === firstId)
    if (first !== undefined) {
      setSelectedId(first.id)
    }
  }

  return (
    <section
      id={LANDING_PAGE_SECTION.capabilities}
      className="section-band-muted"
      aria-labelledby="capabilities-heading"
    >
      <div className="page-shell section-y">
        <Reveal className="max-w-3xl">
          <p className="section-label">{LANDING_PAGE_UI.capabilitiesLabel}</p>
          <h2 id="capabilities-heading" className="section-title mt-3">
            {LANDING_PAGE_UI.capabilitiesTitle}
          </h2>
          <p className="section-body mt-5">{LANDING_PAGE_UI.capabilitiesSubtitle}</p>
        </Reveal>

        <Reveal delay={0.06} className="mt-10">
          <div className="grid gap-8 lg:grid-cols-[minmax(0,0.92fr)_minmax(0,1.35fr)] lg:gap-10 xl:gap-12">
            <div className="min-w-0">
              <div
                className="flex gap-2 overflow-x-auto pb-2 lg:flex-col lg:overflow-visible lg:pb-0"
                role="tablist"
                aria-label={LANDING_PAGE_UI.capabilitiesDomainHint}
              >
                {LANDING_PAGE_CAPABILITY_DOMAINS.map((item) => {
                  const isActive = item.id === domain.id
                  return (
                    <button
                      key={item.id}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => selectDomain(item)}
                      className={
                        isActive
                          ? 'min-w-[14rem] shrink-0 rounded-[var(--radius-card)] border border-accent/30 bg-accentSoft/70 px-4 py-3.5 text-left dark:bg-accentSoft/25 lg:min-w-0 lg:border-l-[3px] lg:border-l-accent lg:border-y-line/10 lg:border-r-line/10'
                          : 'min-w-[14rem] shrink-0 rounded-[var(--radius-card)] border border-line/10 bg-panel px-4 py-3.5 text-left transition-colors hover:border-line/25 lg:min-w-0'
                      }
                    >
                      <p className={`text-sm font-semibold tracking-tight ${isActive ? 'text-ink' : 'text-muted'}`}>
                        {item.label}
                      </p>
                      <p className="mt-1.5 text-xs leading-5 text-muted">{item.description}</p>
                      <p className="mt-2 text-[0.65rem] font-semibold uppercase tracking-[0.12em] text-accent">
                        {item.capabilityIds.length} {LANDING_PAGE_UI.capabilitiesDomainSurfaceCountSuffix}
                      </p>
                    </button>
                  )
                })}
              </div>

              <div
                className="mt-5 flex flex-wrap gap-2"
                role="tablist"
                aria-label={LANDING_PAGE_UI.capabilitiesSelectHint}
              >
                {domainCapabilities.map((cap) => {
                  const isSelected = cap.id === selected.id
                  return (
                    <button
                      key={cap.id}
                      type="button"
                      role="tab"
                      aria-selected={isSelected}
                      onClick={() => setSelectedId(cap.id)}
                      className={
                        isSelected
                          ? 'rounded-[var(--radius-control)] bg-brand px-3.5 py-2 text-sm font-semibold text-white shadow-sm dark:bg-accent dark:text-[rgb(var(--cta-ink))]'
                          : 'rounded-[var(--radius-control)] border border-line/15 bg-panel px-3.5 py-2 text-sm font-semibold text-muted transition-colors hover:border-line/30 hover:text-ink'
                      }
                    >
                      {cap.label}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="min-w-0" aria-live="polite">
              <CapabilityPreview key={selected.id} cap={selected} />
              <p className="mt-5 text-xs font-medium text-muted">
                {LANDING_PAGE_CAPABILITIES.length} {LANDING_PAGE_UI.capabilitiesSurfaceCountLabel}
              </p>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  )
}
